import { useCallback, useState } from 'react';
import classnames from 'clsx';
import { useIntl } from 'react-intl';

import style from './currency-input.module.css';
import { CurrencyService } from '../../../services/currency-service';
import { noop } from '../../default-props/noop';
import { generateDataTestidAttribute } from '../../services/testid-service';

import type { CurrencyInputProps } from './currency-input.d.ts';
import type React from 'react';

const asDecimal = (value: number | string | null | undefined): string | null | undefined => {
  if (value === undefined || value === null) {
    return value;
  }
  return parseFloat(value.toString()).toFixed(2);
};

const isDefaultOrEmpty = (value: number | string | null | undefined, defaultValue: number): boolean => {
  return value === null || value === undefined || value === '' || asDecimal(value) === asDecimal(defaultValue);
};

/**
 * @deprecated Use `CurrencyInput` from `@shipstation/client-common/components/currency-input` instead.
 * See: ui-components/readme-documentation/MIGRATION.md
 */
export const CurrencyInput: React.FC<CurrencyInputProps> = ({
  'aria-label': ariaLabel = '',
  'data-testid': dataTestId = null,
  className = null,
  currencyCode,
  currencySymbolClassName,
  defaultValue = 0,
  disabled = false,
  disallowNegative = false,
  labelId,
  onBlur = noop,
  onChange = noop,
  onFocus: onFocusProp = noop,
  parentLabelId,
  showPlaceHolderForZero = false,
  value,
}) => {
  const intl = useIntl();
  const { symbol: currencySymbol, placement: currencyOrientation } = CurrencyService.getSymbolFromCode(
    currencyCode,
    undefined,
    intl.locale,
  );
  const [focused, setFocused] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);

  const getValue = useCallback(
    (newValue: number | string | null | undefined, isFocused: boolean, hasChangedParam?: boolean): string | number => {
      const changed = hasChangedParam ?? hasChanged ?? false;
      if (!changed && isDefaultOrEmpty(newValue, defaultValue)) {
        return '';
      }
      if (isFocused) {
        return newValue ?? '';
      }
      return asDecimal(newValue) ?? '';
    },
    [defaultValue, hasChanged],
  );

  const getPlaceHolder = useCallback((): string | undefined => {
    if (showPlaceHolderForZero) {
      return '0.00';
    }
  }, [showPlaceHolderForZero]);

  const [stateValue, setStateValue] = useState<string | number>(getValue(value, false));
  const onFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const newValue = getValue(e.target.value, false);
      setFocused(true);
      setStateValue(newValue);
      onFocusProp();
    },
    [getValue, onFocusProp],
  );

  const change = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let newValue: string | number = e.target.value;
      if (disallowNegative && parseFloat(newValue.toString()) < 0) {
        newValue = 0;
      }
      const processedValue = getValue(newValue, true, true);
      onChange(asDecimal(processedValue));
      setHasChanged(true);
      setStateValue(processedValue);
    },
    [onChange, getValue, disallowNegative],
  );

  const blur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const newValue = getValue(e.target.value, false);
      onBlur(newValue);
      onChange(newValue);
      setFocused(false);
      setStateValue(newValue);
    },
    [getValue, onBlur, onChange],
  );

  const definedValue = value ?? 0;
  const realValue = disabled ? (asDecimal(definedValue) ?? '') : stateValue;
  const classes = classnames(
    style['currency-input'],
    {
      [style['symbol-right']]: currencyOrientation === 'right',
      [style.disabled]: disabled,
      [style.focused]: focused,
    },
    className,
  );
  const inputLabel = parentLabelId ? `${parentLabelId} ${labelId}` : labelId;

  return (
    <div className={classes} data-testid='currency-input'>
      <label className={classnames(style.currency, currencySymbolClassName)} data-testid='currency-symbol' id={labelId}>
        {currencySymbol}
      </label>
      <input
        aria-label={ariaLabel}
        aria-labelledby={inputLabel}
        disabled={disabled}
        min={disallowNegative ? 0 : undefined}
        onBlur={blur}
        onChange={change}
        onFocus={onFocus}
        placeholder={getPlaceHolder()}
        type='number'
        value={realValue}
        {...generateDataTestidAttribute(dataTestId)}
      />
    </div>
  );
};
